import { Scan } from 'lucide-react';
import { OVERLAY_ATTRIBUTE, type ZoomPan } from '../board-grid/useZoomPan';

interface ZoomResetButtonProps {
  zoom: ZoomPan;
  label: string;
}

/**
 * The way back out of a zoomed picture, sat in its bottom-left corner.
 *
 * In overlay rather than in the toolbar because it is only ever wanted while the
 * picture is enlarged, and a control that spends most of its life disabled is
 * one the eye stops reading. It appears with the zoom and goes away with it.
 *
 * Deaf to the pointer until it is there, so it cannot swallow a press meant for
 * the wall underneath.
 *
 * Off the view at rest, not off "the picture is bigger than its frame": a photo
 * opened out to a taller frame overflows sideways before anybody has zoomed into
 * it, and there is nothing there to go back out of.
 */
export function ZoomResetButton({ zoom, label }: ZoomResetButtonProps) {
  if (zoom.atRest) return null;

  return (
    <button
      type="button"
      onClick={zoom.reset}
      // It sits on the picture, not in it: the gesture that enlarged the photo
      // has no claim on this click (see useZoomPan's onClickCapture)...
      {...{ [OVERLAY_ATTRIBUTE]: '' }}
      // ...and the press that reaches it must not also reach the surface, which
      // hit-tests presses on the frame this is drawn over.
      onPointerDown={(event) => event.stopPropagation()}
      aria-label={label}
      title={label}
      className="absolute bottom-2 left-2 flex size-9 items-center justify-center rounded-full border border-white/30 bg-black/60 text-white shadow-lg backdrop-blur transition-colors hover:bg-black/75"
    >
      <Scan className="size-4" aria-hidden />
    </button>
  );
}
